/**
 * WABulkModal — "Prepare WhatsApp Messages" bulk action. Select a template,
 * generate a personalised preview per lead, validate phones, then open each
 * conversation manually (no unauthorised auto-blasting).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Loader2, X, ExternalLink, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  ready:         { pt: "Pronto", en: "Ready", cls: "bg-emerald-500/15 text-emerald-500" },
  invalid_phone: { pt: "Telefone inválido", en: "Invalid Phone", cls: "bg-red-500/15 text-red-500" },
  missing_phone: { pt: "Sem telefone", en: "Missing Phone", cls: "bg-gray-500/15 text-gray-400" },
  opted_out:     { pt: "Opt-out", en: "Opted Out", cls: "bg-amber-500/15 text-amber-500" },
};

export default function WABulkModal({ open, onClose, leads, isPt = true }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [openedIds, setOpenedIds] = useState({});
  const [job, setJob] = useState(null);
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  useEffect(() => {
    if (!open) return;
    setItems(null); setCounts({}); setTemplateId(""); setOpenedIds({});
    axios.get(`${API}/wa-templates`)
      .then(({ data }) => setTemplates((data.templates || []).filter((x) => x.active !== false)))
      .catch(() => setTemplates([]));
  }, [open]);

  if (!open) return null;

  const prepare = async () => {
    const ids = (leads || []).map((l) => l.id).filter(Boolean).slice(0, 200);
    if (!ids.length) { toast.error(isPt ? "Nenhum lead selecionado" : "No leads selected"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads/wa/bulk-prepare`, {
        lead_ids: ids, template_id: templateId,
      });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "bulk prepare failed");
    } finally { setLoading(false); }
  };

  const openWA = async (it) => {
    if (!it.wa_link) return;
    window.open(it.wa_link, "_blank", "noopener");
    setOpenedIds((o) => ({ ...o, [it.lead_id]: true }));
    axios.post(`${API}/leads/${it.lead_id}/wa/log`, {
      event: "opened", message_preview: (it.message || "").slice(0, 140),
    }).catch(() => {});
  };

  // Fase 3 — automated Twilio sending (no manual preview needed per lead)
  const autoSend = async () => {
    const readyIds = (items || []).filter((x) => x.status === "ready").map((x) => x.lead_id);
    if (!readyIds.length) { toast.error(isPt ? "Nenhum lead pronto para envio" : "No leads ready"); return; }
    const msg = isPt
      ? `Enviar automaticamente para ${readyIds.length} lead(s) via Twilio WhatsApp? (throttle de 3s entre mensagens)`
      : `Auto-send to ${readyIds.length} lead(s) via Twilio WhatsApp?`;
    if (!window.confirm(msg)) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/leads/wa/bulk-send`, {
        lead_ids: readyIds, template_id: templateId, throttle_seconds: 3,
      });
      if (!data.ok) {
        toast.info(isPt ? "Já existe um envio em andamento" : "A send job is already running");
        setSending(false);
        return;
      }
      setJob({ id: data.job_id, status: "running", total: data.total, sent: 0, skipped: 0, failed: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await axios.get(`${API}/leads/wa/send-jobs/${data.job_id}`);
          setJob(j);
          if (j.status === "done") {
            clearInterval(pollRef.current);
            setSending(false);
            toast.success(isPt
              ? `Envio concluído: ${j.sent} enviados, ${j.skipped} pulados, ${j.failed} falhas`
              : `Done: ${j.sent} sent, ${j.skipped} skipped, ${j.failed} failed`);
          }
        } catch (_e) { /* keep polling */ }
      }, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isPt ? "Falha no envio automático" : "Auto-send failed"));
      setSending(false);
    }
  };

  return (
    <div data-testid="wa-bulk-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="al-card max-w-[760px] w-full p-6 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#25D366]" />
            <div>
              <h2 className="text-[16px] font-bold text-[var(--al-text)]">
                {isPt ? "Preparar mensagens de WhatsApp" : "Prepare WhatsApp Messages"}
              </h2>
              <p className="text-[11.5px] text-[var(--al-text-muted)]">
                {(leads || []).length} {isPt ? "leads selecionados" : "leads selected"} ·{" "}
                {isPt ? "prévia manual OU envio automático via Twilio" : "manual preview OR Twilio auto-send"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--al-text-muted)] hover:text-[var(--al-text)]" data-testid="wa-bulk-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-end gap-2 mb-3">
          <div className="flex-1">
            <label className="block text-[11px] font-semibold text-[var(--al-text-muted)] mb-1">Template</label>
            <select data-testid="wa-bulk-template" value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="al-input h-9 text-[12.5px] w-full">
              <option value="">{isPt ? "— Padrão —" : "— Default —"}</option>
              {templates.map((tp) => (
                <option key={tp.id} value={tp.id}>{tp.name} ({tp.category})</option>
              ))}
            </select>
          </div>
          <button type="button" data-testid="wa-bulk-generate" onClick={prepare} disabled={loading}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#25D366] hover:bg-[#1DA851] text-white text-[12.5px] font-semibold disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {isPt ? "Gerar prévias" : "Generate Previews"}
          </button>
        </div>

        {items && (
          <>
            <div data-testid="wa-bulk-counts" className="flex gap-2 flex-wrap mb-2 text-[10.5px] font-bold items-center">
              {Object.entries(counts).map(([k, v]) => {
                const st = STATUS_STYLE[k] || STATUS_STYLE.missing_phone;
                return (
                  <span key={k} className={`px-2 py-0.5 rounded ${st.cls}`}>
                    {isPt ? st.pt : st.en}: {v}
                  </span>
                );
              })}
              <button type="button" data-testid="wa-bulk-autosend" onClick={autoSend}
                disabled={sending || !(counts.ready > 0)}
                className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#128C7E] hover:bg-[#0e7266] text-white text-[11px] font-bold disabled:opacity-50">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {isPt ? `Enviar automaticamente (${counts.ready || 0})` : `Auto-send (${counts.ready || 0})`}
              </button>
            </div>
            {job && (
              <div data-testid="wa-bulk-job" className="mb-2 rounded-lg border border-[#128C7E]/40 bg-[#128C7E]/5 p-2.5">
                <div className="flex items-center justify-between text-[11px] font-bold mb-1">
                  <span>{job.status === "done"
                    ? (isPt ? "✅ Envio concluído" : "✅ Done")
                    : (isPt ? "📤 Enviando via Twilio..." : "📤 Sending...")}</span>
                  <span data-testid="wa-bulk-job-progress">
                    {(job.sent || 0) + (job.skipped || 0) + (job.failed || 0)}/{job.total} ·{" "}
                    {job.sent || 0} {isPt ? "enviados" : "sent"} · {job.skipped || 0} {isPt ? "pulados" : "skipped"} · {job.failed || 0} {isPt ? "falhas" : "failed"}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-[var(--al-border)] overflow-hidden">
                  <div className="h-full bg-[#128C7E] transition-all"
                    style={{ width: `${Math.min(100, (((job.sent || 0) + (job.skipped || 0) + (job.failed || 0)) / (job.total || 1)) * 100)}%` }} />
                </div>
                {(job.results || []).some((r) => r.status === "failed" && r.hint) && (
                  <p className="text-[10px] text-amber-600 mt-1.5">
                    {(job.results || []).find((r) => r.hint)?.hint}
                  </p>
                )}
              </div>
            )}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {items.map((it) => {
                const st = STATUS_STYLE[it.status] || STATUS_STYLE.missing_phone;
                return (
                  <div key={it.lead_id} data-testid={`wa-bulk-row-${it.lead_id}`}
                    className="rounded-lg border border-[var(--al-border)] bg-[var(--al-row-alt)] p-2.5 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[12.5px] font-semibold text-[var(--al-text)] truncate">{it.name}</span>
                        <span className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded ${st.cls}`}>
                          {isPt ? st.pt : st.en}
                        </span>
                        {openedIds[it.lead_id] && (
                          <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-500">
                            {isPt ? "Aberto no WhatsApp" : "Opened in WhatsApp"}
                          </span>
                        )}
                        <span className="text-[10.5px] text-[var(--al-text-muted)]">{it.phone || "—"}</span>
                      </div>
                      {it.message && (
                        <p className="text-[10.5px] text-[var(--al-text-muted)] mt-1 line-clamp-2">{it.message}</p>
                      )}
                    </div>
                    <button type="button" data-testid={`wa-bulk-open-${it.lead_id}`}
                      disabled={!it.wa_link} onClick={() => openWA(it)}
                      className="shrink-0 inline-flex items-center gap-1 px-2.5 h-8 rounded-lg text-[11px] font-bold text-white disabled:opacity-40"
                      style={{ background: "#25D366" }}>
                      <ExternalLink className="w-3 h-3" /> {isPt ? "Abrir" : "Open"}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
